import '../../highlight';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import QuillCursors from '@minervaproject/quill-cursors';
import ReactQuill, { Quill } from 'react-quill-new';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import 'react-quill-new/dist/quill.snow.css';
import classnames from 'classnames';

import '../../css/quill.scss';
import { getColor } from '../../color';
import LargeFormula from './LargeFormula';
import CustomLinkSanitizer from '../../helpers/quill-link-sanitizer';
import {
  attachIntentTracking,
  shouldBlockUserDelta,
  withTrailingNewline,
} from '../../helpers/editorChangeGuard';

Quill.register('modules/cursors', QuillCursors);
Quill.register(LargeFormula);
Quill.register(CustomLinkSanitizer, true);

// to fix a bug with Quill deleting spell checked words in certain circumstances
// https://app.asana.com/0/1193572723551132/1198698499683760
const Inline = Quill.import('blots/inline');

/**
 * Quill 2.0 made some changes as to how the composition API is handled, which tries to batch
 * an entire IME composition (used in e.g. Chinese/Japanese/Korean) at once without triggering
 * onChange events.
 * This is a problem for us, as we rely on the fact that any change to the editor content immediately
 * triggers an onChange event. If we delay the event, and another user's input comes in, we detect the new
 * content that's still part of the composition as something we just wrote, and send it back to the server,
 * which is then duplicated once we actually commit the composition. Additionally, updates from other users
 * that arrive are seen as local updates, which also causes them to be duplicated back to the sender.
 * If many users are typing with IMEs in the same document, this becomes extremely messy.
 *
 * We tried many solutions (see https://github.com/minervaproject/structured-learning-exercises/pull/1586).
 * The simplest and hopefully lowest-risk solution we found was to patch the Quill instance to force it to
 * only tiny batches and immediately trigger onChange events. This doesn't prevent _all_ issues, but improves
 * the experience enough for it to be, at most, a minor annoyance.
 */
const maybePatchQuillCompositionAPI = (quill) => {
  if (!quill.compositionPatched) {
    quill.scroll.domNode.addEventListener('compositionupdate', () => {
      if (quill.composition.isComposing) {
        quill.scroll.batchEnd();
        quill.scroll.batchStart();
      }
    });
    quill.compositionPatched = true;
  }
};

/**
 * Quill's selection layer (Selection.setNativeRange) unconditionally calls
 * `this.root.focus({ preventScroll: true })` whenever it sets a native range
 * on an unfocused editor. When this component lives inside an iframe (e.g. a
 * Forum/Minerva Doc embedded in Flight), that focus call steals focus from
 * the parent window — for example from a chat input the user is typing in.
 *
 * The focus is triggered both inline (during applyDelta after a remote op)
 * and asynchronously: Quill's syntax module schedules a setTimeout that
 * re-highlights code blocks and then restores the selection, which calls
 * setNativeRange again in a later tick. Because of the async path, patching
 * around individual updateContents calls isn't sufficient — by the time the
 * deferred work runs, the patch has already been removed.
 *
 * The fix is to wrap setNativeRange so that, when the editor doesn't have
 * focus, we lie to the inner `if (!this.hasFocus())` check by temporarily
 * stubbing hasFocus to return true. This skips the focus() call entirely
 * without affecting any other Quill behavior. When the editor does have
 * focus locally, the patch is a passthrough.
 */
const maybePatchQuillSelectionFocus = (quill) => {
  if (quill.selectionFocusPatched) return;
  const selection = quill.selection;
  const origSetNativeRange = selection.setNativeRange.bind(selection);
  const origHasFocus = selection.hasFocus.bind(selection);
  selection.setNativeRange = function patchedSetNativeRange(...args) {
    if (origHasFocus()) {
      return origSetNativeRange(...args);
    }
    selection.hasFocus = () => true;
    try {
      return origSetNativeRange(...args);
    } finally {
      selection.hasFocus = origHasFocus;
    }
  };
  quill.selectionFocusPatched = true;
};

class CustomAttributes extends Inline {
  constructor(domNode, value) {
    super(domNode, value);

    const span = this.replaceWith(new Inline(Inline.create()));

    span.children.forEach((child) => {
      if (child.attributes) child.attributes.copy(span);
      if (child.unwrap) child.unwrap();
    });

    // here we apply every attribute from <font> tag to span as a style
    Object.keys(domNode.attributes).forEach((key) => {
      if (domNode.attributes[key].name !== 'style') {
        const value = domNode.attributes[key].value;
        let name = domNode.attributes[key].name;
        if (name === 'face') name = 'font-family';
        span.format(name, value);
      }
    });

    this.remove();

    return span;
  }
}

CustomAttributes.blotName = 'customAttributes';
CustomAttributes.tagName = 'FONT';

Quill.register(CustomAttributes, true);
// spell check fix end

const reducePush = (ary, item, maxItems) => {
  ary.push(item);
  if (ary.length > maxItems) {
    ary.splice(0, ary.length - maxItems);
  }
};

// avoid pasting of images which are naively inserted into ShareDB doc causing memory issues,
// TODO: fix properly with upload to S3
// list from https://github.com/quilljs/quill/issues/1108
const formats = [
  'background',
  'bold',
  'color',
  'font',
  'code',
  'italic',
  'link',
  'size',
  'strike',
  'script',
  'underline',
  'blockquote',
  'header',
  'indent',
  'list',
  'align',
  'direction',
  'code-block',
  'formula',
  // 'image'
  // 'video'
];

// There is a Rollbar bug that we have had huge problems reproducing, partially because we don't
// have access to the presence messages. Using this global presenceLog object, which gets sent
// to Rollbar on error, we hope to be able to reproduce and fix this bug in the future.
// (Stian Sep 20, 2019)
const presenceLog = [];

// We use userIDs internally to track cursors, but when we receive a disconnect we only receive
// the ShareDB auto-generated session id. Therefore, we make sure to track the mapping between
// userId and ShareDB session Id when we first setup a new cursor, so that we can use this later
// to track which cursor to remove on disconnect.
const IDMapping = {};

// Only record the user's own edits in the undo stack. With the default
// (userOnly: false) the initial load and every remote collaborator's change are
// undoable too, so a single Cmd+Z could broadcast "delete everything" to ShareDB.
const HISTORY_OPTIONS = { userOnly: true };

// How many times per mount we put the editor back in sync after refusing an
// external mutation. Beyond this we keep refusing to submit, but stop resetting
// the editor so a tool that rewrites the DOM in a loop cannot keep us busy.
const MAX_RESYNCS_PER_MOUNT = 20;

class RichTextField extends Component {
  constructor(props) {
    super(props);
    const { path, uuid } = this.props;

    this.path = path || [uuid];
  }

  update = () => {
    const { doc } = this.props;
    const data = get(doc.data, this.path);

    // Bug fix: Quill ignores the last newline, which causes problems
    // with rendering and capturing the latest caption. This copies the
    // delta instead of mutating the ShareDB snapshot in place.
    this._editor.getEditor().setContents(withTrailingNewline(data));
  };

  updateCursor = (range) => {
    const { readOnlyWorksheet } = this.props;

    if (readOnlyWorksheet) {
      return;
    }
    const {
      doc,
      currentUser: { userId },
    } = this.props;
    doc.submitPresence({
      p: this.path,
      t: 'rich-text',
      u: userId,
      s: {
        u: userId,
        c: 0,
        s: [[range.index, range.index + range.length]],
      },
    });
  };

  setUpCursors = () => {
    const { doc, currentUser } = this.props;
    const editor = this._editor.getEditor();
    this.cursors = editor.getModule('cursors');

    if (!currentUser.noPresence) {
      editor.on('selection-change', (range, _, source) => {
        if (range && source === 'user') {
          this.updateCursor(range);
        }
      });
    }

    doc.on('presence', this.onPresence);
  };

  onPresence = (srcList, submitted) => {
    const { doc, currentUser } = this.props;

    reducePush(presenceLog, doc.presence, 30);
    srcList.forEach((src) => {
      const presence = doc.presence[src];
      if (!presence || !presence.p) {
        this.cursors.removeCursor(IDMapping[src]);
        return;
      }

      if (!isEqual(presence.p, this.path)) {
        this.cursors.removeCursor(presence.u);
        return;
      }

      if (presence.s.u) {
        const userId = presence.s.u;
        IDMapping[src] = userId;
        if (
          userId !== currentUser.userId &&
          presence.s.s &&
          presence.s.s.length > 0
        ) {
          // TODO: Can QuillCursors support multiple selections?
          const sel = presence.s.s[0];

          // Use Math.abs because the sharedb presence type
          // supports reverse selections, but I don't think
          // Quill Cursors does.
          const len = Math.abs(sel[1] - sel[0]);
          const min = Math.min(sel[0], sel[1]);

          try {
            this.cursors.createCursor(
              userId,
              doc.data.users[userId].name,
              getColor(userId)
            );
            this.cursors.moveCursor(userId, { index: min, length: len });
            if (submitted) {
              this.cursors.flashCursor(userId);
            }
          } catch (e) {
            if (window.Rollbar) {
              window.Rollbar.error(
                'Error while updating cursors/selections',
                e,
                {
                  userId,
                  index: len,
                  length: min,
                  data: doc.data,
                  presence: presence,
                  docPresence: doc.presence,
                  presenceLog,
                }
              );
            }
          }
        }
      }
    });
  };

  componentDidMount() {
    const { currentUser } = this.props;

    // if we do not want to listen to the ShareDB doc but receive
    // props like a normal component
    if (currentUser.timeline) {
      return;
    }

    this.update();

    this.guardEditor(this._editor.getEditor());

    // disable dropping of images to avoid memory issues with the container -
    // TODO: should eventually be solved with proper upload to S3
    this._editor.getEditor().container.ondrop = () => false;

    // We need to know when our iFrame looses focus, and manually remove focus from
    // Quill, to avoid it stealing focus when receiving updates from other users
    // ref: https://github.com/minervaproject/picasso/pull/5815
    window.addEventListener('message', (msg) => {
      if (
        msg.data === 'focus_state_controller:iframe-blurred' &&
        !readOnlyWorksheet
      ) {
        try {
          this._editor.getEditor().blur();
        } catch (e) {
          console.error('Could not getEditor().blur');
        }
      }
    });

    const { doc, focusLast, readOnlyWorksheet } = this.props;

    doc.on('op', this.onOp);
    this.setUpCursors();
    if (focusLast && !readOnlyWorksheet) {
      // only update cursor after we subscribe to the doc so the presence update gets synced
      const updateCursorInterval = setInterval(() => {
        if (doc.requestReplyPresence === false) {
          clearInterval(updateCursorInterval);
          const quill = this._editor.getEditor();
          quill.setSelection(quill.getLength());
          quill.focus();
          quill.enable();
          quill.setSelection(quill.getLength());
          this.updateCursor(quill.getSelection());
        }
      }, 100);
    }
  }

  onOp = (op, source) => {
    if (source === this._editor) {
      return;
    }

    if (this._editor) {
      const editor = this._editor.getEditor();
      if (!editor) {
        return;
      }

      const { currentUser } = this.props;

      forEach(op, (operation) => {
        // Ensures the ops are for exactly this editor in situations where there
        // are multiple active editors in the page
        if (isEqual(operation.p, this.path)) {
          editor.updateContents(operation.o);
        }
        if (operation.u !== currentUser.userId) {
          this.cursors.flashCursor(operation.u);
        }
      });
    }
  };

  // ReactQuill builds a brand-new Quill instance whenever its `modules` change
  // (e.g. when the worksheet becomes read-only and back), so everything that is
  // attached to a Quill instance has to follow the current one.
  guardEditor = (quill) => {
    if (!quill || quill === this._guardedQuill) {
      return;
    }
    if (this._detachIntentTracking) {
      this._detachIntentTracking();
    }

    // Record real input events so handleOnChange can tell the user's edits apart
    // from DOM rewrites made by translation extensions, spellcheckers and the like.
    const toolbarModule = quill.getModule('toolbar');
    this._detachIntentTracking = attachIntentTracking({
      root: quill.root,
      toolbar: toolbarModule && toolbarModule.container,
      onIntent: this.markIntent,
    });

    // Opt the editor out of page translation. ReactQuill only forwards a fixed set
    // of props to its DOM node, so this cannot be done through JSX, and a wrapper
    // element would break callers that lay the editor out as a flex child.
    quill.container.setAttribute('translate', 'no');

    this._guardedQuill = quill;
  };

  componentDidUpdate() {
    const { currentUser } = this.props;
    if (!currentUser.timeline && this._editor) {
      this.guardEditor(this._editor.getEditor());
    }
  }

  markIntent = () => {
    this._lastIntentAt = Date.now();
  };

  // Quill labels every DOM mutation inside the editor as a `user` change, including
  // rewrites made by translation extensions, spellcheckers and similar tools. Those
  // never fire input events, so a destructive delta without recent intent was not
  // made by the user and must not reach ShareDB, where it would wipe the answer for
  // every collaborator (see "Missing content in breakout document", Aug 2026).
  isExternalMutation = (delta) =>
    shouldBlockUserDelta({
      delta,
      lengthAfter: this._editor.getEditor().getLength(),
      lastIntentAt: this._lastIntentAt,
      now: Date.now(),
    });

  rejectExternalMutation = (delta) => {
    const { doc } = this.props;
    const quill = this._editor.getEditor();

    this._rejectedMutations = (this._rejectedMutations || 0) + 1;
    const limitReached = this._rejectedMutations === MAX_RESYNCS_PER_MOUNT + 1;

    if (window.Rollbar) {
      const details = {
        docId: doc.id,
        path: this.path,
        delta: delta.ops || delta,
        rejected: this._rejectedMutations,
        html: quill.root.innerHTML.slice(0, 2000),
      };
      if (limitReached) {
        window.Rollbar.error(
          'Editor disabled after repeated destructive changes without user input',
          details
        );
      } else {
        window.Rollbar.warning(
          'Blocked destructive editor change without user input',
          details
        );
      }
    }

    // Put the editor back in sync with the shared document. This must happen on
    // every rejection: leaving Quill's model out of step with `doc.data` would
    // make the user's next keystroke submit an op at the wrong offsets.
    this.update();

    // Quill's history already recorded the rejected change as a user edit, and
    // the API-sourced resync above only transforms that entry instead of removing
    // it. Drop it, or a later Cmd+Z would re-apply the stale inverse and duplicate
    // the restored text. This also discards the user's own undo history, which is
    // the lesser evil in this rare situation.
    quill.getModule('history').clear();

    // Something keeps rewriting the editor. Stop accepting input rather than let
    // the user keep typing into an editor that is being fought over; a reload
    // gives them a clean instance.
    if (limitReached) {
      quill.disable();
    }
  };

  handleOnChange = (content, delta, source) => {
    const {
      doc,
      currentUser,
      setChangedToNow = () => {},
      setSavedToNow = () => {},
    } = this.props;
    if (source === 'user') {
      if (this.isExternalMutation(delta)) {
        this.rejectExternalMutation(delta);
        return;
      }

      const op = [
        {
          p: this.path,
          t: 'rich-text',
          o: delta.ops,
          u: currentUser.userId,
        },
      ];

      setChangedToNow();

      doc.submitOp(op, { source: this._editor }, (error) => {
        if (!error) {
          setSavedToNow();
        }
      });
    }
  };

  _storeRef = (ref) => {
    this._editor = ref;
    if (ref && ref.getEditor) {
      const quill = this._editor.getEditor();
      maybePatchQuillCompositionAPI(quill);
      maybePatchQuillSelectionFocus(quill);
    }
  };

  componentWillUnmount() {
    const { doc } = this.props;
    doc.removeListener('presence', this.onPresence);
    doc.removeListener('op', this.onOp);
    if (this._detachIntentTracking) {
      this._detachIntentTracking();
    }
  }

  render() {
    const {
      height,
      embedded,
      readOnlyWorksheet,
      captionsContent,
      darkMode,
      className,
      style,
      uuid,
      path,
      currentUser,
      data,
    } = this.props;

    const toolbarOptions = [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'link', 'blockquote', 'code-block'],
      [
        { list: 'ordered' },
        { list: 'bullet' },
        { indent: '-1' },
        { indent: '+1' },
        { color: [] },
      ],
    ];

    const modules =
      embedded || currentUser.rollup || readOnlyWorksheet || captionsContent
        ? { toolbar: false, cursors: true, history: HISTORY_OPTIONS }
        : {
            cursors: true,
            syntax: true,
            toolbar: toolbarOptions,
            history: HISTORY_OPTIONS,
          };

    const getClassName = () => {
      if (embedded) {
        return null;
      }
      if (currentUser.rollup) {
        return 'rollup body';
      }

      return classnames([
        height,
        className,
        { readonly: readOnlyWorksheet },
        { 'bg-black-tint-20': darkMode },
        { 'text-black-tint-70': darkMode },
        { 'background-light-blue': !darkMode },
        'rich-text-field mb4 rich-text secondary-font font-size-inherit rounded line-height-3',
      ]);
    };

    return (
      <ReactQuill
        {...(currentUser.timeline && { value: data })}
        scrollingContainer="html"
        className={getClassName()}
        style={style}
        id={path}
        readOnly={readOnlyWorksheet}
        onChange={this.handleOnChange}
        ref={this._storeRef}
        modules={modules}
        formats={formats}
      />
    );
  }
}

RichTextField.defaultProps = {
  className: '',
  height: 'medium',
  data: {},
  embedded: false,
  readOnlyWorksheet: false,
  captionsContent: false,
  darkMode: false,
  path: undefined,
  focusLast: undefined,
  style: null,
};

RichTextField.propTypes = {
  doc: PropTypes.object.isRequired,
  uuid: PropTypes.string.isRequired,
  currentUser: PropTypes.object.isRequired,
  data: PropTypes.object,
  height: PropTypes.string,
  className: PropTypes.string,
  style: PropTypes.object,
  readOnlyWorksheet: PropTypes.bool,
  captionsContent: PropTypes.bool,
  darkMode: PropTypes.bool,
  embedded: PropTypes.bool,
  path: PropTypes.arrayOf(PropTypes.string),
  focusLast: PropTypes.bool,
};

export default RichTextField;
